import { useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { Bell } from 'lucide-react-native';

import { Button, Sheet, TextField } from '@/components/ui';
import { crearNotifReponer } from '@/services/firebase/notificaciones';
import { useAuth } from '@/stores/authStore';
import { colors, radius, typography } from '@/theme/tokens';
import type { ProductoVenta, InsumoInterno } from '@/types/domain';

interface Props {
  visible: boolean;
  onClose: () => void;
  producto: ProductoVenta | InsumoInterno | null;
  origen: 'venta' | 'interno';
}

export function AvisarReposicionSheet({ visible, onClose, producto, origen }: Props) {
  const perfil = useAuth((s) => s.perfil);
  const [cantidad, setCantidad] = useState('');
  const [nota, setNota] = useState('');
  const [saving, setSaving] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setCantidad('');
    setNota('');
    setSaving(false);
    setEnviado(false);
    setError(null);
  }, [visible]);

  if (!producto) return null;

  const onSubmit = async () => {
    setError(null);
    if (!perfil) return setError('Sesión inválida.');
    setSaving(true);
    try {
      await crearNotifReponer({
        productoId: producto.id,
        productoNombre: `${producto.nombre} (${producto.presentacion})`,
        origen,
        cantidadSugerida: cantidad ? Number(cantidad) : undefined,
        notaExtra: nota.trim() || undefined,
        creadaPorUid: perfil.uid,
        creadaPorNombre: perfil.nombre,
      });
      setEnviado(true);
      setTimeout(() => onClose(), 1500);
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo enviar la notificación.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      size="sm"
      title="Avisar a la administradora"
      subtitle={`Pedir reposición de ${producto.nombre}`}
      footer={
        enviado ? null : (
          <>
            <Button label="Cancelar" variant="ghost" onPress={onClose} />
            <Button
              label="Enviar aviso"
              iconLeft={<Bell size={16} color={colors.white} />}
              onPress={onSubmit}
              loading={saving}
            />
          </>
        )
      }
    >
      {enviado ? (
        <View style={{ alignItems: 'center', paddingVertical: 16, gap: 8 }}>
          <View
            style={{
              width: 56,
              height: 56,
              borderRadius: 28,
              backgroundColor: colors.success[50],
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Bell size={26} color={colors.success[600]} />
          </View>
          <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 18, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
            Aviso enviado
          </Text>
          <Text style={{ color: colors.ink[500], fontSize: 13, textAlign: 'center' }}>
            La administradora ya lo verá en sus notificaciones.
          </Text>
        </View>
      ) : (
        <>
          <View style={{ padding: 14, borderRadius: radius.xl, backgroundColor: colors.warning[50] }}>
            <Text style={{ color: colors.warning[600], fontSize: 12, fontWeight: typography.weights.semibold, marginBottom: 4 }}>
              Producto: {producto.nombre} ({producto.presentacion})
            </Text>
            <Text style={{ color: colors.ink[700], fontSize: 12 }}>
              Stock actual: {producto.stock} · Mínimo: {producto.stockMin}
            </Text>
          </View>
          <TextField label="Cantidad sugerida (opcional)" placeholder="Ej: 5" value={cantidad} onChangeText={setCantidad} keyboardType="numeric" />
          <TextField label="Nota adicional (opcional)" placeholder="Cliente lo pidió urgente, etc." value={nota} onChangeText={setNota} multiline />
          {error ? (
            <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
              <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{error}</Text>
            </View>
          ) : null}
        </>
      )}
    </Sheet>
  );
}
